import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'

interface UseHistoricalImageryLayerProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  styleLoadedCount: number
  otherMapOptions: any
}

const SOURCE_ID = 'historical-imagery-source'
const LAYER_ID = 'historical-imagery-layer'

/**
 * Hook to manage historical satellite imagery from swisstopo WMTS service
 * Displays SWISSIMAGE tiles for years 1990-2024
 */
export const useHistoricalImageryLayer = ({
  map,
  mapLoaded,
  styleLoadedCount,
  otherMapOptions,
}: UseHistoricalImageryLayerProps) => {
  // Track if layer has been added to prevent duplicate additions
  const layerAddedRef = useRef(false)

  // Get historical imagery layer state from map options
  const historicalImageryState = (otherMapOptions as any)
    .historicalImageryLayerState || {
    selectedYear: 'disabled',
    opacity: 80,
  }

  const { selectedYear, opacity } = historicalImageryState

  // Handle layer addition, updates, and removal
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    const mapInstance = map.current

    // Check if layer should be visible
    const isEnabled = selectedYear !== 'disabled' && selectedYear !== ''

    // Remove layer if disabled
    if (!isEnabled) {
      if (mapInstance.getLayer(LAYER_ID)) {
        mapInstance.removeLayer(LAYER_ID)
      }
      if (mapInstance.getSource(SOURCE_ID)) {
        mapInstance.removeSource(SOURCE_ID)
      }
      layerAddedRef.current = false
      return
    }

    // Build WMTS tile URL with selected year
    // Format: https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swissimage-product/default/YYYY/3857/{z}/{x}/{y}.jpeg
    // Note: The service accepts year only (not YYYYMMDD format)
    const tileUrl = `https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swissimage-product/default/${selectedYear}/3857/{z}/{x}/{y}.jpeg`

    // Add or update source and layer
    if (!mapInstance.getSource(SOURCE_ID)) {
      mapInstance.addSource(SOURCE_ID, {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: 256,
        scheme: 'xyz',
      })
    } else {
      // Update existing source with new tile URL
      const _source = mapInstance.getSource(
        SOURCE_ID
      ) as mapboxgl.RasterTileSource
      // Remove and re-add source to update tiles URL
      if (mapInstance.getLayer(LAYER_ID)) {
        mapInstance.removeLayer(LAYER_ID)
      }
      mapInstance.removeSource(SOURCE_ID)
      mapInstance.addSource(SOURCE_ID, {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: 256,
        scheme: 'xyz',
      })
    }

    if (!mapInstance.getLayer(LAYER_ID)) {
      // Find the first symbol layer to insert imagery below labels
      let firstSymbolId: string | undefined
      try {
        const style = mapInstance.getStyle()
        if (style?.layers) {
          for (const layer of style.layers) {
            if (layer.type === 'symbol') {
              firstSymbolId = layer.id
              break
            }
          }
        }
      } catch {
        // Style not loaded yet, skip
        return
      }

      mapInstance.addLayer(
        {
          id: LAYER_ID,
          type: 'raster',
          source: SOURCE_ID,
          paint: {
            'raster-opacity': opacity / 100,
          },
        },
        firstSymbolId // Insert below labels
      )
      layerAddedRef.current = true
    }
  }, [mapLoaded, styleLoadedCount, selectedYear, opacity])

  // Update opacity when it changes
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    const mapInstance = map.current

    if (mapInstance.getLayer(LAYER_ID)) {
      mapInstance.setPaintProperty(LAYER_ID, 'raster-opacity', opacity / 100)
    }
  }, [mapLoaded, opacity])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (map.current) {
        const mapInstance = map.current
        if (mapInstance.getLayer(LAYER_ID)) {
          mapInstance.removeLayer(LAYER_ID)
        }
        if (mapInstance.getSource(SOURCE_ID)) {
          mapInstance.removeSource(SOURCE_ID)
        }
        layerAddedRef.current = false
      }
    }
  }, [])
}
